import React, { useState, useEffect } from 'react';
import { EmployeeScheduleView as EmployeeScheduleData } from '../../types/maintenance';
import { api } from '../../utils/api';

interface EmployeeScheduleViewProps {
  employeeId: string;
  initialDate?: Date;
  onAccessMaintenance?: (machineId: string, machineCode: string) => void;
}

export const EmployeeScheduleView: React.FC<EmployeeScheduleViewProps> = ({
  employeeId,
  initialDate,
  onAccessMaintenance,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date>(initialDate || new Date());
  const [scheduleData, setScheduleData] = useState<EmployeeScheduleData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSchedule();
  }, [employeeId, selectedDate]);

  const loadSchedule = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      const response = await api.get<EmployeeScheduleData>(
        `/maintenance-schedules/employee/${employeeId}/${dateStr}`
      );
      setScheduleData(response.data);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setScheduleData(null);
        setError(null); // No error for no schedule
      } else {
        setError(err.response?.data?.error || 'Erro ao carregar agenda');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('pt-BR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (dateString: string): string => {
    return new Date(dateString).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleDateChange = (days: number) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    setSelectedDate(newDate);
  };

  const handleTodayClick = () => {
    setSelectedDate(new Date());
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; className: string }> = {
      online: { label: 'Online', className: 'bg-green-100 text-green-800' },
      offline: { label: 'Offline', className: 'bg-gray-100 text-gray-800' },
      maintenance: { label: 'Em Manutenção', className: 'bg-yellow-100 text-yellow-800' },
      in_use: { label: 'Em Uso', className: 'bg-blue-100 text-blue-800' },
    };

    const statusInfo = statusMap[status] || { label: status, className: 'bg-gray-100 text-gray-800' };

    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusInfo.className}`}>
        {statusInfo.label}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Date Selector */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => handleDateChange(-1)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            title="Dia anterior"
          >
            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="text-center">
            <h3 className="text-lg font-bold text-gray-900 capitalize">
              {formatDate(selectedDate)}
            </h3>
            {selectedDate.toDateString() !== new Date().toDateString() && (
              <button
                onClick={handleTodayClick}
                className="text-sm text-orange-600 hover:text-orange-700 font-medium mt-1"
              >
                Voltar para hoje
              </button>
            )}
          </div>

          <button
            onClick={() => handleDateChange(1)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            title="Próximo dia"
          >
            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Schedule Content */}
      {!scheduleData || scheduleData.assignedMachines.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <svg
            className="w-16 h-16 mx-auto text-gray-400 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Nenhuma manutenção agendada
          </h3>
          <p className="text-gray-500">
            Você não possui máquinas atribuídas para manutenção nesta data.
          </p>
        </div>
      ) : (
        <>
          {/* Summary */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Máquinas</p>
                <p className="text-2xl font-bold text-gray-900">
                  {scheduleData.assignedMachines.length}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Concluídas</p>
                <p className="text-2xl font-bold text-green-600">
                  {scheduleData.assignedMachines.filter(m => m.maintenanceCompleted).length}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Pendentes</p>
                <p className="text-2xl font-bold text-orange-600">
                  {scheduleData.assignedMachines.filter(m => !m.maintenanceCompleted).length}
                </p>
              </div>
            </div>
          </div>

          {/* Machine List */}
          <div className="space-y-4">
            {scheduleData.assignedMachines.map((assignment) => (
              <div
                key={assignment.machine.id}
                className={`bg-white rounded-lg shadow p-4 border-l-4 ${
                  assignment.maintenanceCompleted
                    ? 'border-green-500'
                    : 'border-orange-500'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-lg font-bold text-gray-900 font-mono">
                        {assignment.machine.code}
                      </h4>
                      {getStatusBadge(assignment.machine.status)}
                      {assignment.maintenanceCompleted && (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Concluída
                        </span>
                      )}
                    </div>

                    <div className="space-y-1 text-sm text-gray-600">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>{assignment.machine.location}</span>
                      </div>

                      {assignment.maintenanceSession && (
                        <>
                          <div className="flex items-center">
                            <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>
                              Início: {formatTime(assignment.maintenanceSession.startTime)}
                            </span>
                          </div>
                          {assignment.maintenanceSession.endTime && (
                            <div className="flex items-center">
                              <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span>
                                Fim: {formatTime(assignment.maintenanceSession.endTime)}
                                {assignment.maintenanceSession.duration && (
                                  <span className="ml-2 text-gray-500">
                                    ({assignment.maintenanceSession.duration} min)
                                  </span>
                                )}
                              </span>
                            </div>
                          )}
                          {assignment.maintenanceSession.notes && (
                            <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                              <strong>Observações:</strong> {assignment.maintenanceSession.notes}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {!assignment.maintenanceCompleted && onAccessMaintenance && (
                    <button
                      onClick={() => onAccessMaintenance(assignment.machine.id, assignment.machine.code)}
                      className="ml-4 inline-flex items-center px-4 py-2 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition-colors shadow-sm"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Iniciar
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
